import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { courseAPI, categoryAPI, tagAPI, BASE_URL } from '../services/api';
import Select from 'react-select';
import { Skeleton } from '@mui/material';

function Courses() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    category: '',
    tags: ''
  });

  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    if (user && user.role !== 'admin') {
      setError('Access denied. Admin only.');
      navigate('/user');
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchCourses();
  }, [pagination.page, filters]);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const response = await courseAPI.getCourseList(pagination.page, pagination.limit, filters);
      setCourses(response.data.courses);
      setPagination(prev => ({ ...prev, ...response.data.pagination }));
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch courses');
      console.error('Error fetching courses:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCourse = () => {
    navigate('/courses/create');
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const getStatusBadge = (status) => {
    const colors = {
      draft: { bg: '#fef3c7', text: '#92400e' },
      published: { bg: '#c6f6d5', text: '#22543d' },
      archived: { bg: '#e2e8f0', text: '#4a5568' }
    };
    const color = colors[status] || colors.draft;
    
    return (
      <span style={{
        padding: '4px 10px',
        background: color.bg,
        color: color.text,
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '600',
        textTransform: 'capitalize'
      }}>
        {status}
      </span>
    );
  };

  if (loading && courses.length === 0) {
    return (
      <div className="container" style={{ paddingTop: '20px', background: '#ffffff', minHeight: '100vh', borderRadius: '12px', padding: '24px' }}>
        {/* Header skeleton */}
        <div style={{ marginBottom: '24px' }}>
          <Skeleton variant="text" width="40%" height={32} sx={{ marginBottom: '8px' }} />
          <Skeleton variant="text" width="60%" height={20} />
        </div>

        {/* Filters section skeleton */}
        <div style={{
          marginBottom: '20px',
          padding: '16px',
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          border: '1px solid #e2e8f0'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <Skeleton variant="text" width={150} height={24} />
            <Skeleton variant="rectangular" width={140} height={40} sx={{ borderRadius: '6px' }} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
            <Skeleton variant="rectangular" height={42} sx={{ borderRadius: '6px' }} />
            <Skeleton variant="rectangular" height={42} sx={{ borderRadius: '6px' }} />
            <Skeleton variant="rectangular" height={42} sx={{ borderRadius: '6px' }} />
          </div>
        </div>

        {/* Courses Grid Skeleton */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div
              key={i}
              style={{
                border: '1.5px solid #e2e8f0',
                borderRadius: '12px',
                overflow: 'hidden',
                background: '#ffffff'
              }}
            >
              {/* Course Thumbnail Skeleton */}
              <Skeleton variant="rectangular" width="100%" height={200} />
              
              <div style={{ padding: '20px' }}>
                {/* Title and Status */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <Skeleton variant="text" width="60%" height={28} />
                  <Skeleton variant="rectangular" width={60} height={20} sx={{ borderRadius: '5px' }} />
                  <Skeleton variant="rectangular" width={70} height={20} sx={{ borderRadius: '12px' }} />
                </div>
                
                {/* Description */}
                <Skeleton variant="text" width="100%" height={20} sx={{ marginBottom: '8px' }} />
                <Skeleton variant="text" width="80%" height={20} sx={{ marginBottom: '12px' }} />

                {/* Stats */}
                <div style={{ display: 'flex', gap: '16px', marginBottom: '12px' }}>
                  <Skeleton variant="text" width={80} height={18} />
                  <Skeleton variant="text" width={80} height={18} />
                  <Skeleton variant="text" width={80} height={18} />
                </div>

                {/* Tags */}
                <div style={{ display: 'flex', gap: '6px' }}>
                  <Skeleton variant="rectangular" width={60} height={22} sx={{ borderRadius: '16px' }} />
                  <Skeleton variant="rectangular" width={70} height={22} sx={{ borderRadius: '16px' }} />
                  <Skeleton variant="rectangular" width={55} height={22} sx={{ borderRadius: '16px' }} />
                </div>
              </div>

              {/* Action buttons skeleton */}
              <div style={{
                padding: '16px 20px',
                borderTop: '1px solid #e2e8f0',
                display: 'flex',
                gap: '8px'
              }}>
                <Skeleton variant="rectangular" width="50%" height={36} sx={{ borderRadius: '6px' }} />
                <Skeleton variant="rectangular" width="50%" height={36} sx={{ borderRadius: '6px' }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ paddingTop: '20px', background: '#ffffff', minHeight: '100vh', borderRadius: '12px', padding: '24px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ color: '#667eea', fontSize: '22px', marginBottom: '4px' }}>
          📚 Course Management
        </h2>
        <p style={{ color: '#718096', fontSize: '13px', margin: 0 }}>
          Create and manage courses, chapters, and learning materials
        </p>
      </div>

      {error && (
        <div className="error-message" style={{ marginBottom: '16px' }}>
          {error}
        </div>
      )}

      {/* Filters */}
      <div style={{
        marginBottom: '20px',
        padding: '16px',
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#1a202c', margin: 0 }}>
            🔍 Filter Courses
          </h3>
          <button
            className="btn btn-primary"
            onClick={handleCreateCourse}
            style={{ fontSize: '13px', padding: '8px 16px' }}
          >
            + Create Course
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
          <div>
            <input
              type="text"
              placeholder="Search courses..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '1px solid #e2e8f0', background: '#ffffff', color: '#1a202c', fontSize: '14px' }}
            />
          </div>
          
          <div>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: '12px', border: '1px solid #e2e8f0', background: '#ffffff', color: '#1a202c', fontSize: '14px' }}
            >
              <option value="">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          <div>
            <select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              style={{ width: '100%', padding: '10px 12px', borderRadius: '12px', border: '1px solid #e2e8f0', background: '#ffffff', color: '#1a202c', fontSize: '14px' }}
            >
              <option value="">All Categories</option>
              {categories.map(cat => (
                <option key={cat._id} value={cat._id}>{cat.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Courses Grid */}
      {courses.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          border: '2px dashed #cbd5e0'
        }}>
          <p style={{ fontSize: '48px', margin: '0 0 16px 0' }}>📚</p>
          <h3 style={{ margin: '0 0 8px 0', color: '#1a202c', fontSize: '20px' }}>No Courses Yet</h3>
          <p style={{ color: '#4a5568', marginBottom: '20px', fontSize: '15px' }}>
            Create your first course to get started
          </p>
          <button 
            className="btn btn-primary"
            onClick={handleCreateCourse}
          >
            + Create Course
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
          {courses.map(course => (
            <div
              key={course._id}
              style={{
                border: '1.5px solid #e2e8f0',
                borderRadius: '12px',
                overflow: 'hidden',
                background: '#ffffff',
                transition: 'all 0.2s ease',
                display: 'flex',
                flexDirection: 'column'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#667eea';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#e2e8f0';
                e.currentTarget.style.background = '#ffffff';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* Course Thumbnail */}
              {course.thumbnail ? (
                <div style={{
                  width: '100%',
                  overflow: 'hidden',
                  background: '#f7fafc'
                }}>
                  <img 
                    src={course.thumbnail} 
                    alt={course.title}
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block'
                    }}
                  />
                </div>
              ) : (
                <div style={{
                  width: '100%',
                  height: '200px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '48px'
                }}>
                  📚
                </div>
              )}
              
              <div style={{ padding: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#1a202c' }}>
                      {course.title}
                    </h3>
                    <span style={{
                      padding: '2px 8px',
                      background: '#eef2ff',
                      color: '#667eea',
                      borderRadius: '5px',
                      fontSize: '12px',
                      fontWeight: '600'
                    }}>
                      {course.code}
                    </span>
                    {getStatusBadge(course.status)}
                  </div>
                  
                  {course.description && (
                    <p style={{ margin: '0 0 12px 0', color: '#4a5568', fontSize: '14px', lineHeight: '1.5' }}>
                      {course.description}
                    </p>
                  )}

                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', fontSize: '13px', color: '#718096' }}>
                    <span>👤 {course.instructor?.name || 'Unknown'}</span>
                    <span>👥 {course.stats?.enrollmentCount || 0} students</span>
                    <span>📖 {course.stats?.chapterCount || 0} chapters</span>
                    <span>📝 {course.stats?.assetCount || 0} assets</span>
                  </div>

                  {course.tags && course.tags.length > 0 && (
                    <div style={{ marginTop: '12px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {course.tags.map(tag => (
                        <span key={tag._id} style={{
                          padding: '3px 8px',
                          background: '#f7fafc',
                          color: '#4a5568',
                          borderRadius: '16px',
                          fontSize: '11px',
                          fontWeight: '500',
                          border: '1px solid #e2e8f0'
                        }}>
                          {typeof tag === 'object' ? tag.name : tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Action Buttons */}
              <div style={{ 
                padding: '16px 20px', 
                borderTop: '1px solid #e2e8f0',
                display: 'flex',
                gap: '8px',
                flexWrap: 'wrap'
              }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/course-builder/${course._id}`);
                  }}
                  className="btn btn-primary"
                  style={{ 
                    flex: '1 1 120px',
                    fontSize: '13px',
                    padding: '8px 12px'
                  }}
                >
                  📚 Edit Content
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/courses/${course._id}/chapters/progress`);
                  }}
                  className="btn"
                  style={{ 
                    flex: '1 1 120px',
                    fontSize: '13px',
                    padding: '8px 12px',
                    background: '#e6f9f0',
                    border: '1px solid #48bb78',
                    color: '#22543d'
                  }}
                >
                  📊 Track Progress
                </button>
              </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div style={{
          marginTop: '24px',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '8px',
          paddingTop: '24px',
          borderTop: '1px solid #e2e8f0'
        }}>
          <button
            className="btn btn-secondary"
            disabled={pagination.page === 1}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
            style={{ fontSize: '12px', padding: '8px 16px' }}
          >
            ← Previous
          </button>
          
          <span style={{ color: '#4a5568', fontSize: '14px' }}>
            Page {pagination.page} of {pagination.pages}
          </span>
          
          <button
            className="btn btn-secondary"
            disabled={pagination.page === pagination.pages}
            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
            style={{ fontSize: '12px', padding: '8px 16px' }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

export default Courses;

